import { Button, Checkbox, Flex, Select, Text, theme } from '@/components';
import { CloseOutlined } from '@ant-design/icons';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useSiderCommentFilterStorage } from '../../hooks/local-storage';
import { useCommentStackSiderView } from '../../hooks/stack-sider-view';
import {
  CommentPublishTypeLabelModifier,
  commentPublishType,
} from '../../models/comment';
import {
  type CommentFilterParams,
  CommentResolvedStatusLabelModifier,
  commentResolvedStatusType,
} from '../../models/search';
import { PinpointDisplay } from '../pinpoint';

const resolvedStatusOptions = commentResolvedStatusType.options.map(
  (value) => ({
    label: CommentResolvedStatusLabelModifier[value],
    value: value === 'resolved' ? true : value === 'unresolved' ? false : value,
  }),
);

const publishTypeOptions = commentPublishType.options.map((value) => ({
  label: CommentPublishTypeLabelModifier[value],
  value,
}));

export const CommentFilter: FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const stackSiderView = useCommentStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'comment-filter', t('unreachable-code'));

  const { lastSearched, setSearchHistoryStorage } =
    useSiderCommentFilterStorage();

  const [commentsFilterParams, setCommentsFilterParams] =
    useState<CommentFilterParams>({
      publishType: lastSearched?.publishType ?? null,
      resolved: lastSearched?.resolved ?? null,
      readStatus: lastSearched?.readStatus ?? null,
      isCurrentActiveTabOnly: lastSearched?.isCurrentActiveTabOnly ?? false,
      pinpointKey: null,
    });

  useEffect(() => {
    const pinpointKey = siderConfig.pinpointKey || '';
    if (pinpointKey) {
      setCommentsFilterParams((prev) => ({
        ...prev,
        pinpointKey,
      }));
    }
  }, [siderConfig.pinpointKey]);

  return (
    <Flex vertical gap="middle">
      <Flex vertical gap="small">
        <Text weight="regular" text={t('resolve')} color="secondary" />
        <Select
          defaultValue={commentsFilterParams.resolved ?? 'all'}
          onChange={(value) => {
            setCommentsFilterParams((prev) => ({
              ...prev,
              resolved: value === 'all' ? null : (value as boolean),
            }));
          }}
          options={resolvedStatusOptions}
        />
      </Flex>

      <Flex vertical gap="small">
        <Text
          weight="regular"
          text={t('comment.is-unread-label')}
          color="secondary"
        />
        <Checkbox
          checked={commentsFilterParams.readStatus === 'unread'}
          onChange={(e) => {
            setCommentsFilterParams((prev) => ({
              ...prev,
              readStatus: e.target.checked ? 'unread' : null,
            }));
          }}
        >
          {t('comment.unread')}
        </Checkbox>
      </Flex>

      <Flex vertical gap="small">
        <Text
          weight="regular"
          text={t('comment.current-active-tab-only-label')}
          color="secondary"
        />
        <Checkbox
          checked={commentsFilterParams.isCurrentActiveTabOnly}
          onChange={(e) => {
            setCommentsFilterParams((prev) => ({
              ...prev,
              isCurrentActiveTabOnly: e.target.checked,
            }));
          }}
        >
          {t('comment.current-active-tab-only')}
        </Checkbox>
      </Flex>

      <Flex vertical gap="small">
        <Text
          weight="regular"
          text={t('scope-of-disclosure')}
          color="secondary"
        />
        <Select
          defaultValue={commentsFilterParams.publishType ?? 'open'}
          onChange={(value) => {
            setCommentsFilterParams((prev) => ({
              ...prev,
              publishType: value === 'open' ? null : value,
            }));
          }}
          options={publishTypeOptions}
        />
      </Flex>

      <Flex vertical gap="small">
        <Text text={t('comment.pinpoint')} weight="regular" color="secondary" />
        <Flex gap={4}>
          {commentsFilterParams.pinpointKey && siderConfig.pinpoint ? (
            <PinpointDisplay pinpoint={siderConfig.pinpoint} />
          ) : (
            <Text text={null} />
          )}
          {commentsFilterParams.pinpointKey && (
            <CloseOutlined
              style={{
                fontSize: 10,
                padding: 4,
                cursor: 'pointer',
                color: token.colorPrimary,
              }}
              onClick={() => {
                setCommentsFilterParams((prev) => ({
                  ...prev,
                  pinpointKey: null,
                }));
              }}
            />
          )}
        </Flex>
      </Flex>

      <Flex>
        <Button
          type="primary"
          onClick={() => {
            setSearchHistoryStorage({
              ...lastSearched,
              ...commentsFilterParams,
            });
            // 2つstackを削除して新しくセットする
            stackSiderView.set({
              payload: stackSiderView.list.toSpliced(-2, 2).concat({
                siderType: 'comment',
                config: {
                  viewType: 'comment-list',
                  target: siderConfig.target,
                  pinpointKey: commentsFilterParams.pinpointKey,
                  isCurrentActiveTabOnly:
                    commentsFilterParams.isCurrentActiveTabOnly,
                  pinpoint:
                    (commentsFilterParams.pinpointKey &&
                      siderConfig.pinpoint) ||
                    undefined,
                },
              }),
              target: stackSiderView.target,
            });
          }}
        >
          {t('filter-by')}
        </Button>
      </Flex>
    </Flex>
  );
};
